import { forwardRef, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  id?: string;
  className?: string;
}

/**
 * A labelled on/off switch — the Settings view's basic unit.
 *
 * Built as a real `<button role="switch">` rather than a styled checkbox
 * input: it needs to sit in a row with a label and description on its left,
 * which a native checkbox's browser-drawn box can't be restyled to do
 * consistently across browsers.
 */
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onChange, label, description, disabled = false, id, className },
  ref,
) {
  const control = (
    <button
      ref={ref}
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-pill border transition-colors duration-[var(--sg-duration)]',
        'disabled:cursor-not-allowed disabled:opacity-40',
        checked
          ? 'border-silicate/60 bg-silicate/30 shadow-glow-sm'
          : 'border-edge bg-surface-3',
        !disabled && 'cursor-pointer',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'absolute top-0.5 h-4.5 w-4.5 rounded-full bg-content shadow-panel',
          'transition-transform duration-[var(--sg-duration)] ease-hud',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );

  if (!label && !description) return control;

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        {label ? (
          <label
            htmlFor={id}
            onClick={() => !disabled && onChange(!checked)}
            className={cn('block text-sm font-medium text-content', !disabled && 'cursor-pointer')}
          >
            {label}
          </label>
        ) : null}
        {description ? (
          <p className="mt-0.5 text-xs leading-relaxed text-content-muted">{description}</p>
        ) : null}
      </div>
      {control}
    </div>
  );
});
